import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import EmailIcon from "@mui/icons-material/Email";
import api from "../services/api";

const ContactDialog = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return true;
    const re = /^[\d\s\-\+\(\)]+$/;
    return re.test(phone) && phone.replace(/\D/g, "").length >= 10;
  };

  const validateField = (name, value) => {
    let errorMsg = "";

    switch (name) {
      case "name":
        if (!value.trim()) {
          errorMsg = "Name is required";
        } else if (value.trim().length < 2) {
          errorMsg = "Name must be at least 2 characters";
        }
        break;

      case "email":
        if (!value.trim()) {
          errorMsg = "Email is required";
        } else if (!validateEmail(value)) {
          errorMsg = "Please enter a valid email address";
        }
        break;

      case "phone":
        if (value && !validatePhone(value)) {
          errorMsg = "Please enter a valid phone number (at least 10 digits)";
        }
        break;

      case "subject":
        if (!value.trim()) {
          errorMsg = "Subject is required";
        } else if (value.trim().length < 5) {
          errorMsg = "Subject must be at least 5 characters";
        }
        break;

      case "message":
        if (!value.trim()) {
          errorMsg = "Message is required";
        } else if (value.trim().length < 20) {
          errorMsg = "Message must be at least 20 characters";
        }
        break;

      default:
        break;
    }

    return errorMsg;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    if (error) {
      setError(null);
    }
  };

  const handleBlur = (field) => {
    const errorMsg = validateField(field, formData[field]);
    setErrors((prev) => ({
      ...prev,
      [field]: errorMsg,
    }));
  };

  const validateForm = () => {
    const newErrors = {
      name: validateField("name", formData.name),
      email: validateField("email", formData.email),
      phone: validateField("phone", formData.phone),
      subject: validateField("subject", formData.subject),
      message: validateField("message", formData.message),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Please fix the errors in the form");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log("📧 Sending contact form...");

      // Use raw axios for public endpoint (no auth required)
      
      const response = await api.post("/v1/contact", formData);
      console.log("✅ Contact form sent successfully:", response.data);

      if (response.status === 200) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setFormData({
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: "",
          });
          setErrors({
            name: "",
            email: "",
            subject: "",
            message: "",
          });
          setSuccess(false);
        }, 2000);
      }
    } catch (err) {
      console.error("❌ Error sending contact form:", err);
      console.error("Error details:", err.response?.data);

      setError(
        err.response?.data?.message ||
          "Failed to send message. Please try again or email us at support@nephrotrax.com (or sandeep.sinha@verainsight.com)"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSuccess(false);
      setErrors({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EmailIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Contact Us
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Have questions or feedback? We'd love to hear from you! Fill out the
            form below and we'll get back to you as soon as possible.
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Message sent successfully! We'll get back to you soon.
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Full Name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                onBlur={() => handleBlur("name")}
                disabled={loading || success}
                error={!!errors.name}
                helperText={errors.name || "Please enter your full name"}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email Address"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                onBlur={() => handleBlur("email")}
                disabled={loading || success}
                error={!!errors.email}
                helperText={errors.email || "We'll never share your email"}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number (Optional)"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                onBlur={() => handleBlur("phone")}
                disabled={loading || success}
                error={!!errors.phone}
                helperText={errors.phone || "Optional"}
                placeholder="(555) 123-4567"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Subject"
                placeholder="Brief description of your inquiry"
                value={formData.subject}
                onChange={(e) => handleChange("subject", e.target.value)}
                onBlur={() => handleBlur("subject")}
                disabled={loading || success}
                error={!!errors.subject}
                helperText={
                  errors.subject ||
                  "What is your message about? (min 5 characters)"
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={6}
                label="Message"
                placeholder="Please provide details about your inquiry..."
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
                onBlur={() => handleBlur("message")}
                disabled={loading || success}
                error={!!errors.message}
                helperText={
                  errors.message ||
                  `${formData.message.length}/20 characters minimum`
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            disabled={loading || success}
          >
            {loading ? "Sending..." : "Send Message"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ContactDialog;
